import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { environment } from '@env/environment';
import { loginRequest } from '@core/auth/msal.config';
import { useEntitlements } from '@core/access';
import { NAV_DATA } from '@core/nav-data';
import { useRoleDefault } from '@core/settings/useSettings';
import { useSettingsStore } from '@core/state/settings.store';
import { useUserStore } from '@core/state/user.store';
import { useActiveWorkspace } from '@core/state/workspace.store';
import type { NavItem } from '@shared/models/nav-item.model';
import { LOADING_EXIT_AFTER, LOADING_SLOW_AFTER, useLoadingElapsed } from '@ui/Loading';

/**
 * Signing in, as four named steps.
 *
 *   ✓ Authenticated · someone@cushwake.com
 *   ✓ Workspace · APAC → Singapore
 *   ◌ Loading your preferences and flags •••
 *   4 Preparing Today
 *
 * The only full-screen wait in the portal. It covers the moments before there
 * is a shell to draw: the Microsoft 365 redirect, and - on a first visit, when
 * there is no cached user to draw the nav from - the round trip that resolves
 * who the user is. A returning user skips the second part entirely: the shell
 * renders from cache (see `user-cache.ts`) and the content area waits with a
 * skeleton instead.
 *
 * Same clock as every other wait: past five seconds it offers Retry and Use
 * another account, past ten it says which step has not finished. The static
 * copy of this screen in index.html - shown before the bundle has loaded -
 * uses the same classes from `public/assets/boot.css`.
 */

type StepState = 'done' | 'active' | 'todo' | 'failed';

interface Step {
  state: StepState;
  text: string;
  /** What to say when this is the step still running past ten seconds. */
  failure: string;
}

/** The nav title for a path - "Today" for /today - so step 4 can name the destination. */
function titleFor(path: string, items: NavItem[] = NAV_DATA): string | null {
  for (const item of items) {
    if (item.route && item.route.split('?')[0] === path) {
      return item.title;
    }

    const child = item.children && titleFor(path, item.children);

    if (child) {
      return child;
    }
  }

  return null;
}

/** "SG" → "Singapore". Region claims are ISO country codes; anything else passes through. */
function regionName(code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }

  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function BootScreen() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { pathname } = useLocation();

  const userData = useUserStore((state) => state.userData);
  const settingsStatus = useSettingsStore((state) => state.status);
  const entitlements = useEntitlements();
  const workspace = useActiveWorkspace();
  const { landing } = useRoleDefault();

  const elapsed = useLoadingElapsed();

  const email = (instance.getActiveAccount() ?? accounts[0])?.username;
  const destination =
    titleFor(pathname) ?? titleFor(landing && landing.startsWith('/') ? landing.split('?')[0] : '/home') ?? 'your workspace';

  const steps = useMemo<Step[]>(() => {
    const signedIn = isAuthenticated;
    const placed = signedIn && userData !== null;
    const configured = placed && !entitlements.pending && settingsStatus !== 'loading';
    const region = regionName(userData?.region);

    const state = (done: boolean, ready: boolean): StepState => (done ? 'done' : ready ? 'active' : 'todo');

    return [
      {
        state: state(signedIn, true),
        text: signedIn ? `Authenticated${email ? ` · ${email}` : ''}` : 'Signing in with Microsoft 365',
        failure: 'Microsoft 365 has not completed the sign-in.'
      },
      {
        state: state(placed, signedIn),
        text: placed ? `Workspace · ${workspace.label}${region ? ` → ${region}` : ''}` : 'Opening your workspace',
        failure: 'Your account details have not come back from Microsoft Graph.'
      },
      {
        state: state(configured, placed),
        text: 'Loading your preferences and flags',
        failure: 'The Settings service has not answered.'
      },
      {
        state: state(false, configured),
        text: `Preparing ${destination}`,
        failure: `${destination} has not finished loading.`
      }
    ];
  }, [isAuthenticated, userData, entitlements.pending, settingsStatus, email, workspace.label, destination]);

  const activeIndex = steps.findIndex((step) => step.state === 'active');
  const doneCount = steps.filter((step) => step.state === 'done').length;
  const progress = Math.min(100, ((doneCount + (activeIndex >= 0 ? 0.5 : 0)) / steps.length) * 100);

  const slow = elapsed >= LOADING_SLOW_AFTER && activeIndex >= 0;
  const shown = slow ? steps.map((step, index) => (index === activeIndex ? { ...step, state: 'failed' as const } : step)) : steps;

  const retry = () => window.location.reload();
  const switchAccount = () => void instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });

  return (
    <div className="cw-boot" role="status" aria-live="polite" aria-busy="true">
      <div className="cw-boot__inner">
        <img className="cw-boot__logo" src="/assets/images/brand/cw-logo-white.png" alt="Cushman & Wakefield" />
        <h1 className="cw-boot__title">Valuation &amp; Advisory</h1>
        <p className="cw-boot__subtitle">
          {isAuthenticated ? `Getting ${destination} ready` : 'Signing you in with Microsoft 365'}
        </p>

        <div className="cw-boot__bar" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>

        <ol className="cw-boot__steps">
          {shown.map((step, index) => (
            <li key={index} className="cw-boot__step" data-state={step.state}>
              <span className="cw-boot__glyph" aria-hidden="true">
                {step.state === 'done' ? '✓' : step.state === 'failed' ? '!' : step.state === 'todo' ? index + 1 : null}
              </span>
              <span className="cw-boot__text">
                {step.text}
                {step.state === 'active' && (
                  <span className="cw-boot__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                )}
              </span>
            </li>
          ))}
        </ol>

        {slow && (
          <p className="cw-boot__failed" role="alert">
            Still waiting after {Math.round(elapsed / 1000)} s. {steps[activeIndex].failure}
          </p>
        )}

        <p className="cw-boot__help">
          {elapsed >= LOADING_EXIT_AFTER && (
            <>
              Taking longer than usual?{' '}
              <button type="button" onClick={retry}>
                Retry
              </button>{' '}
              ·{' '}
              <button type="button" onClick={switchAccount}>
                Use another account
              </button>
            </>
          )}
        </p>
      </div>

      <p className="cw-boot__foot">
        {environment.production ? 'production' : 'development'} · If this screen persists for more than 10 s we'll show
        what failed.
      </p>
    </div>
  );
}

export default BootScreen;
